import * as React from 'react'
import { Datagrid, DatagridBody, RecordContextProvider } from 'react-admin'
import { TableCell, TableRow, Checkbox, Select, MenuItem } from '@mui/material'
import { default as TextFieldInput } from '@mui/material/TextField'
import { EditableListViewActionsButtons } from '../buttons/EditableListViewActionsButtons'

const getRowStyle = (record) => ({
  backgroundColor:
    record.amount_to_pay > record.amount_pending_converted ? 'rgba(255, 0, 0, 0.1)' : 'inherit',
  boxShadow:
    record.amount_to_pay > record.amount_pending_converted ? '0px 0px 5px 0px rgba(255,0,0,0.5)' : 'none',
  borderLeft: record.amount_to_pay > record.amount_pending_converted ? '3px solid red' : 'none',
})

const CustomDatagridRow = ({ record, id, onToggleItem, children, selected, selectable, ...props }) => {
  const rowStyle = getRowStyle(record)
  return (
    <RecordContextProvider value={record}>
      <TableRow hover={true} style={rowStyle}>
        {props.hasBulkActions && (
          <TableCell padding='none' className='checkbox_table'>
            <Checkbox
              disabled={!selectable}
              checked={selected}
              onClick={(event) => {
                onToggleItem(id, event)
                props.onClickCheckbox && props.onClickCheckbox(id, event)
              }}
            />
          </TableCell>
        )}

        {React.Children.map(children, (field) => {
          let Component = () => field
          const editing = props.editingRows?.includes(id)
          if (field.props.buttons) {
            const showMenu = props.showMenu && props.showMenu(record)
            Component = () => (
              <EditableListViewActionsButtons
                editing={editing}
                showMenu={showMenu}
                onClickMenu={() => props.onClickOpenMenu && props.onClickOpenMenu(id)}
                onClickEdit={() => props.onClickEdit && props.onClickEdit(id)}
                onClickSave={() => props.onClickSave && props.onClickSave(id)}
                onClickCancel={() => props.onClickCancel && props.onClickCancel(id)}
              />
            )
          } else if (field.props.editable && editing) {
            const value = props.editedData?.[id]?.[field.props.source] || record[field.props.source]

            if (field.props.choices) {
              Component = () => (
                <Select
                  size='small'
                  sx={{ minWidth: '200px' }}
                  defaultValue={value}
                  onChange={(e) =>
                    props.onChangeInput && props.onChangeInput(id, field.props.source, e.target.value)
                  }
                  className='input_table'
                >
                  {field.props.choices.map((elem) => (
                    <MenuItem key={elem.id} value={elem[field.props.optionValue]} disabled={elem.disabled}>
                      {elem[field.props.optionText]}
                    </MenuItem>
                  ))}
                </Select>
              )
            } else {
              Component = () => (
                <TextFieldInput
                  size='small'
                  defaultValue={value}
                  onChange={(e) =>
                    props.onChangeInput && props.onChangeInput(id, field.props.source, e.target.value)
                  }
                  className='input_table'
                />
              )
            }
          }

          return (
            <TableCell key={`${id}-${field.props.source}`}>
              <Component />
            </TableCell>
          )
        })}
      </TableRow>
    </RecordContextProvider>
  )
}

const CustomDatagridBody = ({
  editedData,
  editingRows,
  showMenu,
  onClickCheckbox,
  onClickOpenMenu,
  onClickEdit,
  onClickSave,
  onClickCancel,
  onChangeInput,
  ...props
}) => (
  <DatagridBody
    {...props}
    row={
      <CustomDatagridRow
        editedData={editedData}
        editingRows={editingRows}
        showMenu={showMenu}
        onClickCheckbox={onClickCheckbox}
        onClickOpenMenu={onClickOpenMenu}
        onClickEdit={onClickEdit}
        onClickSave={onClickSave}
        onClickCancel={onClickCancel}
        onChangeInput={onChangeInput}
      />
    }
  />
)

export const CustomDatagrid = ({
  editedData,
  editingRows,
  showMenu,
  onClickCheckbox,
  onClickOpenMenu,
  onClickEdit,
  onClickSave,
  onClickCancel,
  onChangeInput,
  ...props
}) => (
  <Datagrid
    {...props}
    body={
      <CustomDatagridBody
        editedData={editedData}
        editingRows={editingRows}
        showMenu={showMenu}
        onClickCheckbox={onClickCheckbox}
        onClickOpenMenu={onClickOpenMenu}
        onClickEdit={onClickEdit}
        onClickSave={onClickSave}
        onClickCancel={onClickCancel}
        onChangeInput={onChangeInput}
      />
    }
  />
)
